import type { RefObject } from "react";
import { useCallback, useEffect, useRef } from "react";
import { normalizeWheelDeltaPx, panByPixels, zoomAboutCursorFrac } from "./timeCameraMath";

/** The unclamped domain a wheel gesture maps to (zoom about cursor, then horizontal pan). Shared by the live preview and the committed value so they never diverge. */
const composeWheelDomain = (
  domain: [number, number],
  scale: number,
  cursorFrac: number,
  panDxPx: number,
  plotWidthPx: number
): [number, number] => {
  let next = scale !== 1 ? zoomAboutCursorFrac(domain, cursorFrac, scale) : domain;
  if (panDxPx !== 0) next = panByPixels(next, panDxPx, plotWidthPx);
  return next;
};

export interface TimeCameraGestureArgs {
  xDomain: [number, number];
  plotWidthPx: number;
  /** Absolute SVG x of the plot's left edge (the y-axis gutter width). The live zoom pivot must live in the same coordinate space as the transformed content, which is offset by this; defaults to 0. */
  plotLeftPx?: number;
  groupRef: RefObject<SVGGElement | null>;
  /**
   * A second group that must ride the same live transform — an overlay drawn ON TOP of the camera
   * surface, which cannot live inside `groupRef` and still be hoverable. Without it such an overlay
   * stands still through the gesture and jumps into place on commit.
   */
  overlayRef?: RefObject<SVGGElement | null> | undefined;
  onCommit: (domain: [number, number]) => void;
  panBoundsMs?: [number, number];
  /** Resolve moving bounds when a gesture commits, without ticking the data source. */
  getPanBoundsMs?: (() => [number, number]) | undefined;
  settleMs?: number;
  zoomIntent?: (e: WheelEvent) => boolean;
  onGestureStart?: () => void;
  /** Per-frame live viewport (ms domain) the gesture previews, or null when the gesture settles/clears. Lets the date axis re-tick against the live window in lockstep with the transformed data. */
  onFramePreview?: (domain: [number, number] | null) => void;
  /**
   * Does this pointer press start a zoom-to-selection instead of a pan?
   *
   * Default: shift, control or command held on the primary button. 🔴 On macOS control+click is
   * the SECONDARY button and also raises a context menu, so control only reaches here on the
   * platforms where it stays a primary click — shift is the modifier that works everywhere.
   */
  selectIntent?: (e: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean; button: number }) => boolean;
  /** The live selection as fractions of the plot width, ascending, or null when it ends. */
  onSelectPreview?: (range: [number, number] | null) => void;
}

const clampPan = (d: [number, number], b?: [number, number]): [number, number] => {
  if (!b) return d;
  const span = d[1] - d[0];
  // Window wider than the clamp range can't preserve its span — pin to the bounds.
  if (span >= b[1] - b[0]) return [b[0], b[1]];
  let [s, e] = d;
  if (e > b[1]) {
    e = b[1];
    s = e - span;
  }
  if (s < b[0]) {
    s = b[0];
    e = s + span;
  }
  return [s, e];
};

const scheduleFrame = (f: FrameRequestCallback): number =>
  typeof requestAnimationFrame === "function"
    ? requestAnimationFrame(f)
    : (setTimeout(() => f(0), 0) as unknown as number);

const cancelFrame = (id: number): void => {
  if (typeof cancelAnimationFrame === "function") cancelAnimationFrame(id);
  else clearTimeout(id);
};

export function useTimeCameraGesture(args: TimeCameraGestureArgs) {
  const { groupRef, overlayRef } = args;

  // Latest props live in refs so the pointer handlers stay referentially stable
  // and the wheel listener (attached once per surface node via the callback ref
  // below) never re-subscribes on a parent re-render. A re-render mid-settle
  // therefore can't tear down the listener and silently drop a pending commit.
  const live = useRef({ xDomain: args.xDomain, plotWidthPx: args.plotWidthPx, plotLeftPx: args.plotLeftPx ?? 0 });
  live.current = { xDomain: args.xDomain, plotWidthPx: args.plotWidthPx, plotLeftPx: args.plotLeftPx ?? 0 };
  const onCommitRef = useRef(args.onCommit);
  onCommitRef.current = args.onCommit;
  const panBoundsRef = useRef(args.panBoundsMs);
  panBoundsRef.current = args.panBoundsMs;
  const getPanBoundsRef = useRef(args.getPanBoundsMs);
  getPanBoundsRef.current = args.getPanBoundsMs;
  const settleRef = useRef(args.settleMs ?? 140);
  settleRef.current = args.settleMs ?? 140;
  const zoomIntentRef = useRef(args.zoomIntent ?? ((e: WheelEvent) => e.ctrlKey));
  zoomIntentRef.current = args.zoomIntent ?? ((e: WheelEvent) => e.ctrlKey);
  const onGestureStartRef = useRef(args.onGestureStart);
  onGestureStartRef.current = args.onGestureStart;
  const onFramePreviewRef = useRef(args.onFramePreview);
  onFramePreviewRef.current = args.onFramePreview;
  const defaultSelectIntent = (e: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean; button: number }) =>
    e.button === 0 && (e.shiftKey || e.ctrlKey || e.metaKey);
  const selectIntentRef = useRef(args.selectIntent ?? defaultSelectIntent);
  selectIntentRef.current = args.selectIntent ?? defaultSelectIntent;
  const onSelectPreviewRef = useRef(args.onSelectPreview);
  onSelectPreviewRef.current = args.onSelectPreview;

  const drag = useRef<{ startX: number; dxPx: number } | null>(null);
  /** Plot-relative x of the press and of the pointer now, in px. */
  const select = useRef<{ startX: number; currentX: number; leftPx: number } | null>(null);
  // Wheel keeps its OWN accumulators (panDxPx, scale) so it never reads or
  // corrupts the pointer drag's startX/dxPx, and vice versa.
  const wheel = useRef<{
    scale: number;
    cursorFrac: number;
    panDxPx: number;
    timer: ReturnType<typeof setTimeout> | null;
  }>({ scale: 1, cursorFrac: 0.5, panDxPx: 0, timer: null });
  const rafId = useRef<number | null>(null);
  const pendingFrame = useRef<{ translateX: number; scaleX: number; preview: [number, number] } | null>(null);
  const gestureActive = useRef(false);

  // The data group rides the GPU transform (cheap for large series); the axis
  // re-ticks against `preview` (the live ms domain). Both are written in the
  // same rAF so the date line and the data never tear apart between frames.
  const writeFrame = useCallback(
    (translateX: number, scaleX: number, preview: [number, number]) => {
      pendingFrame.current = { translateX, scaleX, preview };
      if (rafId.current != null) return;
      rafId.current = scheduleFrame(() => {
        rafId.current = null;
        const p = pendingFrame.current;
        if (p != null) {
          const transform = `translate(${p.translateX},0) scale(${p.scaleX},1)`;
          groupRef.current?.setAttribute("transform", transform);
          overlayRef?.current?.setAttribute("transform", transform);
          onFramePreviewRef.current?.(p.preview);
        }
      });
    },
    [groupRef, overlayRef]
  );

  const clearTransform = useCallback(() => {
    if (rafId.current != null) {
      cancelFrame(rafId.current);
      rafId.current = null;
    }
    pendingFrame.current = null;
    groupRef.current?.removeAttribute("transform");
    overlayRef?.current?.removeAttribute("transform");
    onFramePreviewRef.current?.(null);
  }, [groupRef, overlayRef]);

  const beginGesture = useCallback(() => {
    if (gestureActive.current) return;
    gestureActive.current = true;
    onGestureStartRef.current?.();
  }, []);

  const endGesture = useCallback(() => {
    gestureActive.current = false;
  }, []);

  const clearWheelTimer = useCallback(() => {
    if (wheel.current.timer != null) {
      clearTimeout(wheel.current.timer);
      wheel.current.timer = null;
    }
  }, []);

  const resetWheel = useCallback(() => {
    wheel.current = { scale: 1, cursorFrac: 0.5, panDxPx: 0, timer: null };
  }, []);

  // Zoom and horizontal pan can both accumulate within one wheel-idle window,
  // so commit BOTH (zoom about the cursor, then pan the resulting span) instead
  // of dropping whichever didn't "win".
  const commitWheel = useCallback(() => {
    const { xDomain: d, plotWidthPx: pw } = live.current;
    const { scale, cursorFrac, panDxPx } = wheel.current;
    if (scale === 1 && panDxPx === 0) return;
    onCommitRef.current(
      clampPan(
        composeWheelDomain(d, scale, cursorFrac, panDxPx, pw),
        getPanBoundsRef.current?.() ?? panBoundsRef.current
      )
    );
  }, []);

  // Starting a pointer drag flushes any pending wheel settle as its own
  // committed gesture, so a stale wheel timer can never fire a second commit
  // after the drag has already committed.
  const flushWheel = useCallback(() => {
    if (wheel.current.timer == null) return;
    clearWheelTimer();
    commitWheel();
    resetWheel();
    clearTransform();
  }, [clearWheelTimer, commitWheel, resetWheel, clearTransform]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      flushWheel();
      e.currentTarget.setPointerCapture?.(e.pointerId);
      // Opt-in: without a preview sink the caller has nowhere to draw the band, and a zoom with no
      // visual feedback is worse than the pan it replaced. Keeps every ChartContainer chart as-is.
      if (onSelectPreviewRef.current && selectIntentRef.current(e)) {
        const leftPx = (e.currentTarget as Element).getBoundingClientRect().left;
        const x = e.clientX - leftPx;
        select.current = { startX: x, currentX: x, leftPx };
        onSelectPreviewRef.current?.([0, 0]);
        beginGesture();
        return;
      }
      drag.current = { startX: e.clientX, dxPx: 0 };
      beginGesture();
    },
    [flushWheel, beginGesture]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (select.current) {
        select.current.currentX = e.clientX - select.current.leftPx;
        const w = Math.max(1, live.current.plotWidthPx);
        const a = Math.min(1, Math.max(0, select.current.startX / w));
        const b = Math.min(1, Math.max(0, select.current.currentX / w));
        onSelectPreviewRef.current?.(a <= b ? [a, b] : [b, a]);
        return;
      }
      if (!drag.current) return;
      drag.current.dxPx = e.clientX - drag.current.startX;
      const { xDomain, plotWidthPx } = live.current;
      writeFrame(drag.current.dxPx, 1, panByPixels(xDomain, drag.current.dxPx, plotWidthPx));
    },
    [writeFrame]
  );

  /** Below this the press reads as a click, not a range — zooming to it would be a 2px window. */
  const MIN_SELECT_PX = 8;

  const onPointerUp = useCallback(
    (_e: React.PointerEvent) => {
      if (select.current) {
        const { startX, currentX } = select.current;
        select.current = null;
        onSelectPreviewRef.current?.(null);
        endGesture();
        if (Math.abs(currentX - startX) < MIN_SELECT_PX) return;
        const { xDomain, plotWidthPx } = live.current;
        const w = Math.max(1, plotWidthPx);
        const span = xDomain[1] - xDomain[0];
        const toMs = (x: number) => xDomain[0] + Math.min(1, Math.max(0, x / w)) * span;
        const a = toMs(Math.min(startX, currentX));
        const b = toMs(Math.max(startX, currentX));
        onCommitRef.current(clampPan([a, b], getPanBoundsRef.current?.() ?? panBoundsRef.current));
        return;
      }
      if (!drag.current) return;
      const { dxPx } = drag.current;
      drag.current = null;
      clearTransform();
      endGesture();
      if (dxPx === 0) return;
      onCommitRef.current(
        clampPan(
          panByPixels(live.current.xDomain, dxPx, live.current.plotWidthPx),
          getPanBoundsRef.current?.() ?? panBoundsRef.current
        )
      );
    },
    [clearTransform, endGesture]
  );

  // The browser can revoke the pointer (tab switch, system gesture, lost
  // capture) without a pointerup; abort the drag without committing and clear
  // the live transform + active flag so tooltips aren't suppressed forever.
  const onPointerCancel = useCallback(() => {
    if (select.current) {
      select.current = null;
      onSelectPreviewRef.current?.(null);
      endGesture();
      return;
    }
    if (!drag.current) return;
    drag.current = null;
    clearTransform();
    endGesture();
  }, [clearTransform, endGesture]);

  // Callback ref: attach the non-passive wheel listener when the surface node
  // mounts and detach when it unmounts or changes. This is deliberately NOT a
  // useEffect — effect cleanup runs on every dependency change, which would
  // drop a pending settle timer whenever the parent re-rendered mid-gesture.
  const wheelDetach = useRef<(() => void) | null>(null);
  const wheelTargetRef = useCallback(
    (el: SVGElement | null) => {
      wheelDetach.current?.();
      wheelDetach.current = null;
      if (!el) return;
      const handler = (e: WheelEvent) => {
        // A pointer drag owns the surface; ignore wheel input until it ends so
        // the two paths never run at once.
        if (drag.current) return;
        const { plotWidthPx: w, plotLeftPx: left } = live.current;
        const dy = normalizeWheelDeltaPx(e.deltaY, e.deltaMode);
        const dx = normalizeWheelDeltaPx(e.deltaX, e.deltaMode);
        const isZoom = zoomIntentRef.current(e);
        if (!isZoom && dx === 0) return;
        e.preventDefault();
        beginGesture();
        if (isZoom) {
          // The gesture surface rect IS the plot area, so (clientX - rect.left)
          // is already plot-relative — don't subtract the plot margin again.
          const frac = Math.min(1, Math.max(0, (e.clientX - el.getBoundingClientRect().left) / Math.max(1, w)));
          wheel.current.scale *= Math.exp(-dy / 200);
          wheel.current.cursorFrac = frac;
        } else {
          wheel.current.panDxPx -= dx;
        }
        // One combined transform so a pan tick never drops the live zoom preview
        // (and vice versa): translate carries both the zoom pivot and the pan.
        // cursorFrac is plot-local, but the transformed content sits in absolute
        // coords starting at `left`, so the zoom must pivot about the cursor's
        // ABSOLUTE pixel (left + cursorFrac*w); otherwise it snaps by left*(1-scale)
        // when the gesture commits to the cursor-time-invariant domain.
        const { scale, cursorFrac, panDxPx } = wheel.current;
        writeFrame(
          (left + cursorFrac * w) * (1 - scale) + panDxPx,
          scale,
          composeWheelDomain(live.current.xDomain, scale, cursorFrac, panDxPx, w)
        );
        clearWheelTimer();
        wheel.current.timer = setTimeout(() => {
          commitWheel();
          resetWheel();
          clearTransform();
          endGesture();
        }, settleRef.current);
      };
      el.addEventListener("wheel", handler, { passive: false });
      wheelDetach.current = () => el.removeEventListener("wheel", handler);
    },
    [writeFrame, clearTransform, clearWheelTimer, commitWheel, resetWheel, beginGesture, endGesture]
  );

  // Unmount: detach the listener and cancel any pending frame + settle timer.
  useEffect(
    () => () => {
      wheelDetach.current?.();
      wheelDetach.current = null;
      clearTransform();
      if (wheel.current.timer != null) clearTimeout(wheel.current.timer);
    },
    [clearTransform]
  );

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    wheelTargetRef,
    gestureActiveRef: gestureActive,
  };
}
